import React from 'react';
import * as Sentry from '@sentry/react';

let reportedErrors: string[] = [];

interface ReportContext {
  reportLocation: {
    filePath: string;
    lineNumber: number;
    functionName: string;
  };
}

export async function reportErrorHandler({
  error,
  context,
  msgContext
}: {
  error: Error;
  context?: ReportContext;
  msgContext?: string;
}) {
  const errorMessage = msgContext
    ? `msgContext: ${msgContext}`
    : `msg: ${error.message} | stack: ${error.stack}`;

  if (reportedErrors.includes(errorMessage)) {
    console.warn('Error already reported:', error);
    return;
  }

  try {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      scope.setTag(
        'service',
        `fogo-cruzado-cms-${process.env.REACT_APP_ENV_FLAVOR?.toLowerCase()}`
      );

      if (msgContext) {
        scope.setContext('error_details', {
          msgContext
        });
      }

      if (context?.reportLocation) {
        scope.setContext('report_location', {
          filePath: context.reportLocation.filePath,
          lineNumber: context.reportLocation.lineNumber,
          functionName: context.reportLocation.functionName
        });
      }

      Sentry.captureException(error);
    });

    reportedErrors.push(errorMessage);
  } catch (captureError) {
    console.error('Reporting: ', captureError);
  }
}

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const context = {
      reportLocation: {
        filePath: errorInfo.componentStack,
        lineNumber: 0,
        functionName: error?.name || ''
      }
    };

    if (process.env.REACT_APP_ENV_FLAVOR === 'DEV') {
      console.error(error, errorInfo);
    }

    reportErrorHandler({
      error,
      context,
      msgContext: errorInfo.componentStack
    });

    this.setState({ hasError: true });
  }

  render() {
    return this.props.children;
  }
}

function GlobalErrorHandler({ children }: Props) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

export default GlobalErrorHandler;
